import type { PropsWithChildren } from "react";
import { Navigate } from "react-router-dom";

import { useFreshMe } from "@/api/auth";
import { userHasRight } from "@/lib/rights";

interface RequireRightProps extends PropsWithChildren {
  /** Right name to check; mirrors the server's `require_right(...)`. */
  right: string;
  /** Where to bounce on a missing-right verdict. Defaults to "/". */
  fallback?: string;
}

/**
 * Hard gate for any right. Fires a fresh `/web/api/me` on every
 * mount and refuses to render children until the server's answer
 * is in — defeats "edit React Query cache to add `owner`" tricks
 * even on routes that haven't yet hit a server endpoint of their
 * own. The server still gates every privileged endpoint; this only
 * keeps the SPA from rendering an admin shell to a non-admin.
 *
 * Owner short-circuits via `userHasRight` (matches the server).
 */
export function RequireRight({
  right,
  fallback = "/",
  children,
}: RequireRightProps) {
  const me = useFreshMe();

  // Block render only on the cold-cache case (no data yet). Don't
  // also block on `isFetching` — if two RequireRights are nested
  // (RequireOwner inside RequireAdmin for /admin/reports etc.) the
  // inner mount triggers refetchOnMount, which flips the shared
  // query into `isFetching=true`, which makes the OUTER RequireRight
  // return null, which unmounts the inner, which on next render
  // remounts and re-triggers a fetch — infinite loop.
  //
  // Once we have any data we trust the cached rights for the
  // RequireRight gate. The actual security boundary is the FastAPI
  // gate at /assets/admin/* (the lazy chunk fetch): a tampered cache
  // that briefly lets us render past this gate still 403s on the
  // chunk and the RouteChunkBoundary redirects home.
  if (me.isPending) {
    return null;
  }

  if (me.isError || !userHasRight(me.data?.rights, right)) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}
